/**
 * ЕДИНСТВЕННОЕ место, где живут ключи запросов. Строк-ключей по коду быть не должно:
 * рассыпанные по компонентам литералы расходятся при первом же переименовании, и
 * инвалидация после мутации тихо перестаёт попадать в нужный кэш.
 *
 * Ключи иерархические: queryKeys.tasks.all префиксом входит в queryKeys.tasks.lookup(id),
 * поэтому инвалидация по префиксу гасит всю ветку одним вызовом.
 */
export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },
  subscription: {
    all: ['subscription'] as const,
    status: () => [...queryKeys.subscription.all, 'status'] as const,
  },
  /** Публичный каталог тарифов. До редизайна — `price-cards`, вместе с адресом ручки. */
  plans: {
    all: ['plans'] as const,
    list: () => [...queryKeys.plans.all, 'list'] as const,
    byId: (id: number) => [...queryKeys.plans.all, 'by-id', id] as const,
  },
  tasks: {
    all: ['tasks'] as const,
    lookup: (id: string) => [...queryKeys.tasks.all, 'lookup', id] as const,
    example: () => [...queryKeys.tasks.all, 'example'] as const,
  },
  payments: {
    all: ['payments'] as const,
    latest: () => [...queryKeys.payments.all, 'latest'] as const,
  },
  legal: {
    all: ['legal'] as const,
    document: (slug: string) => [...queryKeys.legal.all, 'document', slug] as const,
  },
  /**
   * Заявка на удаление аккаунта. Своя ветка, а не подмешивание в `auth`: выход из аккаунта
   * гасит `auth`, а заявка к сессии отношения не имеет — она про аккаунт целиком.
   */
  accountDeletion: {
    all: ['account-deletion'] as const,
    mine: () => [...queryKeys.accountDeletion.all, 'mine'] as const,
  },
  /**
   * Админка (волна 08). Отдельная ветка верхнего уровня, а не подмешивание в ветки выше:
   * при выходе из админки кэш гасится одним `removeQueries({ queryKey: queryKeys.admin.all })`,
   * и данные другого пространства токенов не должны при этом задеваться.
   */
  admin: {
    all: ['admin'] as const,
    me: () => [...queryKeys.admin.all, 'me'] as const,
    dashboard: () => [...queryKeys.admin.all, 'dashboard'] as const,
    /**
     * Аналитика. Ключи РАЗДЕЛЬНЫЕ по панелям, как и эндпоинты: переключатель периода
     * обязан перезапросить только то, что от периода зависит, — выручка и когорты
     * считаются за фиксированные двенадцать месяцев и от него не меняются.
     */
    analytics: {
      overview: (period: string) =>
        [...queryKeys.admin.all, 'analytics', 'overview', period] as const,
      revenue: () => [...queryKeys.admin.all, 'analytics', 'revenue'] as const,
      retention: () => [...queryKeys.admin.all, 'analytics', 'retention'] as const,
      engagement: (period: string) =>
        [...queryKeys.admin.all, 'analytics', 'engagement', period] as const,
    },
    /** Ключ несёт оба критерия: поиск по id и по email — разные ответы. */
    userSearch: (byId: number | undefined, byEmail: string | undefined, cursor: number | undefined) =>
      [...queryKeys.admin.all, 'users', 'search', byId ?? null, byEmail ?? null, cursor ?? null] as const,
    task: (id: string) => [...queryKeys.admin.all, 'tasks', id] as const,
    plans: () => [...queryKeys.admin.all, 'price-cards'] as const,
    finance: () => [...queryKeys.admin.all, 'finance'] as const,
    campaigns: () => [...queryKeys.admin.all, 'campaigns'] as const,
    audit: (entityType: string | undefined, entityId: string | undefined, cursor: number | undefined) =>
      [...queryKeys.admin.all, 'audit', entityType ?? null, entityId ?? null, cursor ?? null] as const,
    auditEntry: (id: number) => [...queryKeys.admin.all, 'audit', 'entry', id] as const,
    /** Лента заявок на удаление аккаунта: фильтр по статусу плюс курсор страницы. */
    deletions: (status: string | undefined, cursor: number | undefined) =>
      [...queryKeys.admin.all, 'account-deletions', status ?? null, cursor ?? null] as const,
  },
} as const;
